import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UserButton, useClerk } from "@clerk/react";
import { toast } from "sonner";
import { Clock, ShieldOff } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { SiteHeader, Spinner } from "@/components/Layout";
import { useOwnerApi, errMsg, pub } from "@/lib/api";
import SetupPanel from "./SetupPanel";
import SettingsPanel from "./SettingsPanel";
import MenuEditor from "./MenuEditor";
import SalesPanel from "./SalesPanel";
import EventsPanel from "./EventsPanel";
import StaffPanel, { OrderScreenButton } from "./StaffPanel";

const CreateVenue = ({ api, onCreated }) => {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("cafe");
  const [pilotOpen, setPilotOpen] = useState(false);
  const fromPilot = localStorage.getItem("lg_signup_source") === "pilot_link";
  useEffect(() => { pub.get("/pilot").then((r) => setPilotOpen(r.data.open)).catch(() => {}); }, []);
  const create = async () => {
    try {
      const r = await api.post("/venues", { name, kind, origin_url: window.location.origin, ...(fromPilot ? { source: "pilot_link" } : {}) });
      localStorage.removeItem("lg_signup_source");
      onCreated(r.data); toast.success("Venue created");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-lg mx-auto mt-10 max-w-lg p-6" data-testid="create-venue-card">
      <h2 className="text-2xl font-black">{pilotOpen ? "Set up your pilot venue" : "Set up your venue"}</h2>
      <p className="mt-1 text-sm text-muted2" data-testid="create-venue-note">{pilotOpen
        ? "You're joining the LineGone pilot: no activation fee, practice mode for training, and a choice of Stripe card payments or manual approval with pay at pickup. Real orders open once LineGone approves your account."
        : "After this you'll pay the one-time activation (if it applies), connect Stripe and wait for LineGone approval."}</p>
      <Input className="mt-4" placeholder="Venue name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-venue-name-input" />
      <Select value={kind} onValueChange={setKind}>
        <SelectTrigger className="mt-3" data-testid="new-venue-kind-select"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="cafe">Café</SelectItem><SelectItem value="food_truck">Food truck</SelectItem><SelectItem value="event">Events organiser</SelectItem></SelectContent>
      </Select>
      <button className="btn-primary mt-4 w-full" disabled={name.trim().length < 2} onClick={create} data-testid="create-venue-btn">Create venue</button>
    </div>
  );
};

function useReturnHandlers(api, setVid, reload) {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const vid = params.get("venue");
    if (!vid) return;
    setVid(vid);
    const run = async () => {
      try {
        if (params.get("stripe")) { await api.post(`/venues/${vid}/stripe/refresh`); toast.success("Stripe status updated"); }
        if (params.get("activation")) { await api.post(`/venues/${vid}/activation/sync`); toast.success("Activation payment received"); }
      } catch (e) { toast.error(errMsg(e)); }
      setParams({}, { replace: true });
      reload();
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

const Suspended = ({ msg }) => {
  const { signOut } = useClerk();
  return (
    <div className="card-lg mx-auto mt-16 max-w-md p-8 text-center" data-testid="account-suspended">
      <ShieldOff size={40} className="mx-auto text-danger" />
      <h2 className="mt-3 text-2xl font-black">{msg}</h2>
      <button className="btn-secondary mt-6" onClick={() => signOut()} data-testid="suspended-signout-btn">Sign out</button>
    </div>
  );
};

const PendingBanner = () => (
  <div className="mb-4 flex items-start gap-3 rounded-2xl bg-amber-100 p-4 text-amber-900" data-testid="pending-approval-banner">
    <Clock size={20} className="mt-0.5 shrink-0" />
    <div><div className="font-bold">Your account is awaiting approval.</div><div className="text-sm">You can set up your venue and use practice mode now. Real customer orders open once LineGone approves your account.</div></div>
  </div>
);

const ACTIVE = ["awaiting_approval", "paid", "preparing", "ready"];

const OrdersBar = ({ api, venue }) => {
  const [n, setN] = useState(null);
  useEffect(() => {
    const load = () => api.get(`/venues/${venue.id}/orders`).then((r) => setN(r.data.filter((o) => ACTIVE.includes(o.status)).length)).catch(() => setN(-1));
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [api, venue.id]);
  return (
    <div className={`mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl p-4 ${n > 0 ? "bg-ready text-ink" : "bg-brand-pale"}`} data-testid="orders-bar">
      <div>
        <div className="font-heading text-lg font-black" data-testid="orders-bar-count">{n === null ? "Checking orders…" : n < 0 ? "Couldn't check orders" : n ? `${n} order${n > 1 ? "s" : ""} in progress` : "No orders waiting"}</div>
        <div className="text-sm">Accept, start, mark ready and hand over orders on the order screen.</div>
      </div>
      <OrderScreenButton api={api} venue={venue} className="btn-primary !px-6 !py-3" />
    </div>
  );
};

export default function Dashboard() {
  const api = useOwnerApi();
  const [me, setMe] = useState(null);
  const [venues, setVenues] = useState(null);
  const [vid, setVid] = useState(null);
  const [suspended, setSuspended] = useState(null);
  const load = useCallback(async () => {
    try {
      const [m, v] = await Promise.all([api.get("/me"), api.get("/venues")]);
      setMe(m.data); setVenues(v.data);
      setVid((cur) => cur || v.data[0]?.id || null);
    } catch (e) { if (e?.response?.status === 403 && /suspended/i.test(errMsg(e))) setSuspended(errMsg(e)); else toast.error(errMsg(e)); }
  }, [api]);
  useEffect(() => { load(); document.title = "Venue dashboard · LineGone"; }, [load]);
  useReturnHandlers(api, setVid, load);

  const venue = venues?.find((v) => v.id === vid);
  const right = <UserButton />;
  if (suspended) return <><SiteHeader right={right} /><Suspended msg={suspended} /></>;
  if (!venues) return <><SiteHeader right={right} /><Spinner /></>;
  return (
    <div className="min-h-screen bg-white" data-testid="owner-dashboard">
      <SiteHeader right={right} />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {me?.status === "pending" && <PendingBanner />}
        {venues.length === 0 ? <CreateVenue api={api} onCreated={(v) => { setVenues([v]); setVid(v.id); }} /> : <>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div><div className="eyebrow">Venue dashboard</div><h1 className="text-3xl font-black sm:text-4xl" data-testid="dashboard-venue-name">{venue?.name}</h1></div>
            {venues.length > 1 && <Select value={vid} onValueChange={setVid}>
              <SelectTrigger className="w-64" data-testid="venue-switcher"><SelectValue /></SelectTrigger>
              <SelectContent>{venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.is_demo ? " (demo)" : ""}</SelectItem>)}</SelectContent>
            </Select>}
          </div>
          {venue && <OrdersBar api={api} venue={venue} key={`bar-${venue.id}`} />}
          {venue && <Tabs defaultValue="setup" className="mt-6" key={venue.id}>
            <TabsList className="no-scrollbar flex h-auto w-full justify-start overflow-x-auto rounded-full bg-brand-pale p-1">
              {[["setup", "Setup & QR"], ["settings", "Profile & settings"], ["menu", "Menu"], ["sales", "Orders & sales"], ["staff", "Staff"], ["events", "Events"]].map(([k, l]) =>
                <TabsTrigger key={k} value={k} className="rounded-full px-4 py-2 font-bold data-[state=active]:bg-brand data-[state=active]:text-white" data-testid={`tab-${k}`}>{l}</TabsTrigger>)}
            </TabsList>
            <TabsContent value="setup"><SetupPanel api={api} venue={venue} reload={load} /></TabsContent>
            <TabsContent value="settings"><SettingsPanel api={api} venue={venue} reload={load} /></TabsContent>
            <TabsContent value="menu"><MenuEditor api={api} venue={venue} reload={load} /></TabsContent>
            <TabsContent value="sales"><SalesPanel api={api} venue={venue} /></TabsContent>
            <TabsContent value="staff"><StaffPanel api={api} venue={venue} /></TabsContent>
            <TabsContent value="events"><EventsPanel api={api} venue={venue} /></TabsContent>
          </Tabs>}
        </>}
      </main>
    </div>
  );
}
